/**
 * Persistent token storage (survives reloads / app restarts).
 *
 * Two backends, chosen at runtime:
 *  - **Tauri desktop**: the OS keychain, via the Rust commands registered in
 *    `src-tauri/src/storage.rs` (`save_token` / `load_token` / `clear_token`).
 *    JWTs never touch web storage in the packaged app — they live in the keychain.
 *  - **Browser / Vite dev**: `localStorage` fallback so the dev web flow also keeps
 *    the session across reloads. (Acceptable for local dev; the shipped product is
 *    the Tauri shell, which uses the keychain.)
 *
 * The in-memory cache in `auth-token.ts` stays the synchronous source of truth for
 * request signing; this module only handles the slow, async persistence layer.
 * Errors are swallowed by callers (persistence is best-effort — a keychain hiccup
 * must never break login). Tokens are never logged here.
 */

const ACCESS_KEY = "fad.auth.accessToken";
const REFRESH_KEY = "fad.auth.refreshToken";

export interface PersistedTokens {
  accessToken: string;
  refreshToken: string | null;
}

/** True when running inside the Tauri webview (keychain commands available). */
export function isTauriRuntime(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** Lazily import the Tauri core so the browser bundle never eagerly loads it. */
async function tauriInvoke<T>(cmd: string, args: Record<string, unknown>): Promise<T> {
  const { invoke } = await import("@tauri-apps/api/core");
  return invoke<T>(cmd, args);
}

/** `localStorage` if present (guarded for the node test/SSR environments). */
function webStorage(): Storage | null {
  try {
    return typeof globalThis !== "undefined" && globalThis.localStorage ? globalThis.localStorage : null;
  } catch {
    return null;
  }
}

export async function persistTokens(accessToken: string, refreshToken: string | null): Promise<void> {
  if (isTauriRuntime()) {
    await tauriInvoke("save_token", { key: ACCESS_KEY, value: accessToken });
    if (refreshToken) await tauriInvoke("save_token", { key: REFRESH_KEY, value: refreshToken });
    else await tauriInvoke("clear_token", { key: REFRESH_KEY });
    return;
  }
  const ls = webStorage();
  if (!ls) return;
  ls.setItem(ACCESS_KEY, accessToken);
  if (refreshToken) ls.setItem(REFRESH_KEY, refreshToken);
  else ls.removeItem(REFRESH_KEY);
}

export async function loadPersistedTokens(): Promise<PersistedTokens | null> {
  if (isTauriRuntime()) {
    const accessToken = await tauriInvoke<string | null>("load_token", { key: ACCESS_KEY });
    if (!accessToken) return null;
    const refreshToken = await tauriInvoke<string | null>("load_token", { key: REFRESH_KEY });
    return { accessToken, refreshToken: refreshToken ?? null };
  }
  const ls = webStorage();
  if (!ls) return null;
  const accessToken = ls.getItem(ACCESS_KEY);
  if (!accessToken) return null;
  return { accessToken, refreshToken: ls.getItem(REFRESH_KEY) };
}

export async function clearPersistedTokens(): Promise<void> {
  if (isTauriRuntime()) {
    await tauriInvoke("clear_token", { key: ACCESS_KEY });
    await tauriInvoke("clear_token", { key: REFRESH_KEY });
    return;
  }
  const ls = webStorage();
  if (!ls) return;
  ls.removeItem(ACCESS_KEY);
  ls.removeItem(REFRESH_KEY);
}
